import React from 'react'
import PropTypes from 'prop-types'

import { Box, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import { TrackButton } from 'artkive/components/Tracker'
import usePageScrollTracker from 'artkive/components/Tracker/usePageScrollTracker'
import { FRAMED_MOSAIC, GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import {
  BeforeAfterSection,
  BoxDueDisclaimer,
  CtaButton,
  FeatureQuoteSection,
  HeroDivider,
  HeroDividerWrapper,
  HeroTitle,
  ImageCardRow,
  KeepsakeDigitalStorage,
  KeepsakeFaqs,
  KeepsakeGetStarted,
  KeepsakeHero,
  KeepsakeSection,
  KeepsakeTestimonial,
  Phrases,
  ProductFeatures,
  RecipientCards,
} from 'artkive/pages/Keepsakes'
import theme from 'artkive/utils/theme'

import BrushStrokes from 'images/ecom/brand/brushStrokes.svg'
import Bubbles from 'images/ecom/bubbles.svg'
import TestimonialImage from 'images/ecom/mosaics/Mosaic-Testimonial-2.png'
import ProductLG from 'images/ecom/mosaics/Mosaics-lg.jpg'
import Product from 'images/ecom/mosaics/Mosaics-md.jpg'
import ProductMobile from 'images/ecom/mosaics/Mosaics-sm.jpg'

import {
  asterisks,
  beforeAfter,
  FAQS,
  galleryWallFeatures,
  imageBgPosition,
  keepsakeQuote,
  mosaicHowItWorks,
  mosaicsProductBullets,
  recipientGroups,
  rooms,
  slides,
  testimonialUser,
  whoItsFor,
} from './Mosaics.constants'
import { AlsoLove, FeaturesBubbles, FeaturesSprite, FeaturesTitle, HeroCta, MosaicHowItWorks } from './Mosaics.style'
import StackedMosaicPrices from './StackedMosaicPrices'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    overflowX: 'hidden',
    backgroundColor: theme.colors.sky[100],
  },
}))

const Mosaics = ({ large, small }) => {
  usePageScrollTracker()
  const classes = useStyles()
  const muiTheme = useTheme()
  const isMobile = useMobileBreakpoint()
  const { boxCheckoutUrl } = useSale()

  return (
    <div className={classes.root}>
      <KeepsakeHero
        image={
          <picture>
            <source srcSet={ProductLG} media={muiTheme.breakpoints.up('xl').replace('@media ', '')} />
            <source srcSet={Product} media={muiTheme.breakpoints.up('md').replace('@media ', '')} />
            <source srcSet={ProductMobile} />
            <img src={Product} alt='Framed Art Mosaic of kids art' />
          </picture>
        }
        productBg={theme.colors.bg.purple}
        imageStyle='contain'
      >
        <HeroTitle variant='h1' align={isMobile ? 'center' : 'left'}>
          <Phrases text='Framed Art Mosaic: | A Keepsake You | Proudly Display' />
        </HeroTitle>
        <Box lineHeight={1.6} mt={2} mb={3}>
          <Typography>
            Twenty-five tiny masterpieces, one wall, one frame, forever out of the drawer. The art your child made this
            year, kept exactly where you&apos;ll see it every day.
          </Typography>
        </Box>
        {isMobile && (
          <HeroDividerWrapper>
            <HeroDivider />
          </HeroDividerWrapper>
        )}
        {mosaicsProductBullets.map(({ text }, index) => (
          <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
        ))}
        <StackedMosaicPrices large={large} small={small} asterisks={asterisks} />
        <HeroCta display='flex' alignItems='center' style={{ justifyContent: isMobile ? 'center' : 'left' }}>
          <TrackButton
            color='primary'
            href={boxCheckoutUrl}
            size='large'
            variant='contained'
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{
              section: 'product_description_section',
            }}
          >
            Get My Box
          </TrackButton>
        </HeroCta>
      </KeepsakeHero>
      <ImageCardRow {...rooms} data-testid='room-cards' />
      <BeforeAfterSection {...beforeAfter} data-testid='before-after' />
      <KeepsakeSection>
        <KeepsakeTestimonial
          imageSrc={TestimonialImage}
          quote='The final products were fantastic! The framed mosaic (just arrived today-very well packaged) and the book
                was so well made. I really liked that I got to preview everything and add labels to the pages online
                before it printed.'
          user={testimonialUser}
          imageBgPosition={imageBgPosition}
        />
      </KeepsakeSection>
      <MosaicHowItWorks
        id='how-it-works'
        title='How To Make | Your Mosaic'
        subtitle='From pile to gallery wall in four steps'
        steps={mosaicHowItWorks}
      >
        <Box justifyContent='center' display='flex' pt={6}>
          <CtaButton
            color='primary'
            href={boxCheckoutUrl}
            size='large'
            variant='contained'
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{ section: 'how_it_works' }}
          >
            Get My Box
          </CtaButton>
        </Box>
      </MosaicHowItWorks>
      <BoxDueDisclaimer />
      <ProductFeatures
        title={
          <FeaturesTitle variant='h2' align='center'>
            <Phrases text='A Whole New Kind | Of Gallery Wall' />
          </FeaturesTitle>
        }
        features={galleryWallFeatures}
      >
        <FeaturesSprite src={BrushStrokes} alt='' />
        <FeaturesBubbles src={Bubbles} alt='' />
      </ProductFeatures>
      <FeatureQuoteSection {...keepsakeQuote} data-testid='keepsake-quote' />
      <RecipientCards {...whoItsFor} groups={recipientGroups} data-testid='who-its-for' />
      <KeepsakeDigitalStorage mobileBottomSpacing={theme.spacings.sectionMobile} />
      <KeepsakeGetStarted />
      <KeepsakeFaqs faqs={FAQS} trackerEvent={FRAMED_MOSAIC.FAQ_ITEM_OPEN} />
      <AlsoLove slides={slides} />
    </div>
  )
}

Mosaics.propTypes = {
  large: PropTypes.shape({
    non_bundle_price: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  small: PropTypes.shape({
    non_bundle_price: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
}

export default withApp(Mosaics)
